import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { PushNotifications } from '@capacitor/push-notifications'
import type { ActionPerformed, PushNotificationSchema, Token } from '@capacitor/push-notifications'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import type { DeviceToken } from '@/types/database'

const TOKEN_STORAGE_KEY = 'gogrov_push_token'

type DevicePlatform = DeviceToken['platform']

interface StoredToken {
  token: string
  platform: DevicePlatform
  // false once the token has been stashed but not yet accepted by Supabase —
  // registration commonly completes before a session exists, and the device
  // may be offline at launch. flushPendingDeviceToken() retries these.
  synced: boolean
}

export type PushPermission = 'unsupported' | 'prompt' | 'granted' | 'denied'

export interface PushEvents {
  onToken?: (token: string) => void
  onReceived?: (notification: PushNotificationSchema) => void
  onTapped?: (action: ActionPerformed) => void
  onError?: (error: string) => void
}

export function isPushSupported(): boolean {
  return Capacitor.isNativePlatform()
}

export function devicePlatform(): DevicePlatform {
  const p = Capacitor.getPlatform()
  return p === 'ios' || p === 'android' ? p : 'web'
}

async function readStoredToken(): Promise<StoredToken | null> {
  const { value } = await Preferences.get({ key: TOKEN_STORAGE_KEY })
  if (!value) return null
  try {
    const parsed = JSON.parse(value) as Partial<StoredToken>
    if (!parsed.token) return null
    return {
      token: parsed.token,
      platform: parsed.platform ?? devicePlatform(),
      synced: parsed.synced === true,
    }
  } catch {
    return null
  }
}

/**
 * Upserts on conflict (token): the same device returns the same token on every
 * launch, and the row may still be owned by whoever was signed in last.
 * Returns false (never throws) when there is no session yet or the write fails,
 * so the caller can leave the on-device copy marked unsynced and retry.
 */
async function uploadDeviceToken(token: string, platform: DevicePlatform): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession()
    const userId = data.session?.user.id
    if (!userId) return false

    const { error } = await supabase
      .from('device_tokens')
      .upsert(
        { user_id: userId, client_id: useAuthStore.getState().clientId, token, platform },
        { onConflict: 'token' }
      )

    if (error) {
      console.warn('[GoGrov push] Device token upload failed:', error.message)
      return false
    }
    return true
  } catch (err) {
    console.warn('[GoGrov push] Device token upload failed:', err)
    return false
  }
}

/**
 * Stashes the token on-device first, then tries to upload it. The local copy is
 * the fallback: registration can fire before sign-in (no session → RLS rejects
 * the insert) or while offline, and the OS won't hand us the token again just
 * because we failed to store it.
 */
export async function persistDeviceToken(token: string, platform: string): Promise<void> {
  const mapped: DevicePlatform =
    platform === 'ios' || platform === 'android' ? platform : platform === 'web' ? 'web' : devicePlatform()

  const write = (synced: boolean) =>
    Preferences.set({
      key: TOKEN_STORAGE_KEY,
      value: JSON.stringify({ token, platform: mapped, synced } satisfies StoredToken),
    })

  await write(false)
  if (await uploadDeviceToken(token, mapped)) await write(true)
}

/**
 * Re-uploads the stored token. Called when the session or client changes: an
 * earlier attempt may have been rejected for lack of a session, or have landed
 * with a stale client_id (the profile loads after the session). The upsert is
 * idempotent, so this runs unconditionally rather than only when unsynced.
 */
export async function flushPendingDeviceToken(): Promise<void> {
  const stored = await readStoredToken()
  if (!stored) return

  if (await uploadDeviceToken(stored.token, stored.platform)) {
    await Preferences.set({
      key: TOKEN_STORAGE_KEY,
      value: JSON.stringify({ ...stored, synced: true } satisfies StoredToken),
    })
  }
}

export async function getStoredDeviceToken(): Promise<string | null> {
  return (await readStoredToken())?.token ?? null
}

export async function clearStoredDeviceToken(): Promise<void> {
  await Preferences.remove({ key: TOKEN_STORAGE_KEY })
}

export async function checkPushPermission(): Promise<PushPermission> {
  if (!isPushSupported()) return 'unsupported'
  const status = await PushNotifications.checkPermissions()
  return status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'prompt'
}

/**
 * Attach listeners then request + register. Listeners must be attached before
 * register() — the token callback can fire before register() resolves.
 * Returns a teardown that removes the listeners.
 */
export async function registerPush(events: PushEvents = {}): Promise<{
  permission: PushPermission
  teardown: () => Promise<void>
}> {
  if (!isPushSupported()) {
    return { permission: 'unsupported', teardown: async () => {} }
  }

  const handles = await Promise.all([
    PushNotifications.addListener('registration', (token: Token) => {
      // A failed upload must never escape the listener and crash the app.
      void persistDeviceToken(token.value, Capacitor.getPlatform()).catch((err) => {
        console.warn('[GoGrov push] Could not persist device token:', err)
      })
      events.onToken?.(token.value)
    }),
    PushNotifications.addListener('registrationError', (err: { error: string }) => {
      events.onError?.(err.error)
    }),
    PushNotifications.addListener('pushNotificationReceived', (n: PushNotificationSchema) => {
      events.onReceived?.(n)
    }),
    PushNotifications.addListener('pushNotificationActionPerformed', (a: ActionPerformed) => {
      events.onTapped?.(a)
    }),
  ])

  const teardown = async () => {
    await Promise.all(handles.map((h) => h.remove()))
  }

  let status = await PushNotifications.checkPermissions()
  if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
    status = await PushNotifications.requestPermissions()
  }

  if (status.receive !== 'granted') {
    await teardown()
    return { permission: status.receive === 'denied' ? 'denied' : 'prompt', teardown: async () => {} }
  }

  await PushNotifications.register()
  return { permission: 'granted', teardown }
}

export async function removeAllDeliveredNotifications(): Promise<void> {
  if (!isPushSupported()) return
  await PushNotifications.removeAllDeliveredNotifications()
}
